import { cn } from '@amore/ui/cn'
import { Button } from '@amore/ui/components/button'
import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { Select, SelectItem } from '@amore/ui/components/select'
import { Spinner } from '@amore/ui/components/spinner'
import { Textarea } from '@amore/ui/components/textarea'
import { useNavigate } from '@tanstack/react-router'
import { BookOpen, Flag, NotebookPen, Shapes, Sparkles, UserRound } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { newId } from '@/lib/format'
import { supplierStatusLabels } from '@/lib/labels'
import { useUpdate, useWedding } from '@/mock/store'
import type { CatalogService, Supplier, SupplierProfile, SupplierStatus } from '@/mock/types'
import { Segmented } from '@/patterns/detail'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { catalogMatches, type FoundSupplier, isLink, lookUp } from './lookup'
import { categoryLabel, categoryOrder } from './model'

const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'

/** A new supplier starts in one of these; Zrušeno makes no sense for a new one. */
const startStatuses = (['inquiry', 'agreed', 'booked'] as SupplierStatus[]).map((value) => ({
  value,
  label: supplierStatusLabels[value],
}))

const foundFields: { key: keyof SupplierProfile; label: string }[] = [
  { key: 'website', label: 'Web' },
  { key: 'email', label: 'E-mail' },
  { key: 'phone', label: 'Telefon' },
  { key: 'address', label: 'Adresa' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
]

type Step = 'find' | 'found' | 'manual'
type Manual = {
  name: string
  category: string | null
  contact: { name: string; phone: string; email: string }
  notes: string
}

/**
 * Adding a supplier takes one field: a name or a link to their web or social
 * profile. Catalog matches show while typing; "Najít" asks the AI to look the
 * supplier up on the internet and fill in the profile, which the couple only
 * checks and confirms. "Vyplnit ručně" is the fallback form.
 */
export function AddSupplierSheet({ onClose }: { onClose: () => void }) {
  const data = useWedding()
  const update = useUpdate()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [step, setStep] = useState<Step>('find')
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [found, setFound] = useState<FoundSupplier | null>(null)
  const [status, setStatus] = useState<SupplierStatus>('inquiry')
  const [manual, setManual] = useState<Manual>({
    name: '',
    category: null,
    contact: { name: '', phone: '', email: '' },
    notes: '',
  })
  const [errors, setErrors] = useState<{ query?: boolean; name?: boolean; category?: boolean }>({})
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const matches = step === 'find' ? catalogMatches(data.catalog, query) : []
  const dirty = Boolean(query || found || manual.name || manual.notes)

  const search = () => {
    if (!query.trim()) {
      setErrors({ query: true })
      return
    }
    setSearching(true)
    timer.current = window.setTimeout(() => {
      setFound(lookUp(query.trim()))
      setSearching(false)
      setStep('found')
    }, 1400)
  }
  const toManual = (name = isLink(query) ? '' : query.trim()) => {
    window.clearTimeout(timer.current)
    setSearching(false)
    setManual((current) => ({ ...current, name: current.name || name }))
    setErrors({})
    setStep('manual')
  }
  const back = () => {
    setFound(null)
    setErrors({})
    setStep('find')
  }

  const added = (supplier: Supplier, description: string) => {
    update((current) => ({ ...current, suppliers: [...current.suppliers, supplier] }))
    showToast({
      title: 'Dodavatel přidán',
      description,
      tone: 'success',
      action: {
        label: 'Otevřít',
        altText: 'Otevřít přidaného dodavatele',
        onClick: () => void navigate({ to: `/suppliers/${supplier.id}` }),
      },
    })
    onClose()
  }
  const base = () => ({
    id: newId('sup'),
    status,
    statusChangedAt: '2026-10-19',
    contacts: [],
    notes: '',
  })
  const addFromCatalog = (item: CatalogService) =>
    added(
      { ...base(), name: item.name, category: item.category, catalogServiceId: item.id },
      `${item.name}, z katalogu`,
    )
  const addFound = () => {
    if (!found?.name.trim()) {
      setErrors({ name: true })
      return
    }
    added(
      { ...base(), name: found.name.trim(), category: found.category, profile: found.profile },
      found.name.trim(),
    )
  }
  const addManual = () => {
    const next = { name: !manual.name.trim(), category: !manual.category }
    setErrors(next)
    if (next.name || !manual.category) return
    const { contact } = manual
    const person = contact.name.trim() || contact.phone.trim() || contact.email.trim()
    added(
      {
        ...base(),
        name: manual.name.trim(),
        category: manual.category,
        contacts: person ? [{ id: newId('contact'), ...contact, role: '' }] : [],
        notes: manual.notes,
        profile: {},
      },
      manual.name.trim(),
    )
  }

  const headingClass =
    '-mx-2 w-[calc(100%+1rem)] rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors placeholder:text-muted-foreground/60 hover:bg-muted/60 focus:bg-muted/60'
  const heading =
    step === 'find' ? (
      <div className="space-y-2">
        <textarea
          value={query}
          rows={1}
          data-autofocus
          aria-label="Jméno dodavatele nebo odkaz"
          aria-invalid={errors.query || undefined}
          placeholder="Jméno nebo odkaz na web"
          onChange={(event) => {
            setQuery(event.target.value)
            setErrors({})
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              search()
            }
          }}
          className={cn('field-sizing-content resize-none', headingClass)}
        />
        {errors.query ? (
          <p className="text-sm text-error-foreground">Napište jméno nebo vložte odkaz.</p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Stačí jméno, web nebo Instagram. Najdeme ho v katalogu nebo na internetu a profil
            vyplníme za vás.
          </p>
        )}
      </div>
    ) : (
      <div className="space-y-1">
        <input
          value={step === 'found' ? (found?.name ?? '') : manual.name}
          aria-label="Název dodavatele"
          aria-invalid={errors.name || undefined}
          placeholder="Jak se dodavatel jmenuje?"
          data-autofocus={step === 'manual' || undefined}
          onChange={(event) => {
            const name = event.target.value
            if (step === 'found') setFound((current) => current && { ...current, name })
            else setManual((current) => ({ ...current, name }))
            setErrors((current) => ({ ...current, name: false }))
          }}
          className={headingClass}
        />
        {errors.name ? (
          <p className="text-sm text-error-foreground">Doplňte název dodavatele.</p>
        ) : null}
      </div>
    )

  return (
    <DetailSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      eyebrow="Nový dodavatel"
      title="Nový dodavatel"
      dirty={dirty}
      titleContent={heading}
      primaryAction={
        step === 'find'
          ? { label: searching ? 'Hledáme…' : 'Najít', onClick: search, disabled: searching }
          : { label: 'Přidat dodavatele', onClick: step === 'found' ? addFound : addManual }
      }
    >
      {step === 'find' ? (
        <div className="space-y-7">
          {matches.length > 0 ? (
            <CatalogMatches
              matches={matches}
              mine={(item) => data.suppliers.find((s) => s.catalogServiceId === item.id)}
              onAdd={addFromCatalog}
              onOpen={(id) => {
                onClose()
                void navigate({ to: `/suppliers/${id}` })
              }}
            />
          ) : null}
          {searching ? (
            <div className="flex items-center gap-3 rounded-2xl bg-muted/60 px-4 py-4 text-sm">
              <Spinner className="text-action" />
              <span>Hledáme „{query.trim()}“ na internetu a skládáme profil…</span>
            </div>
          ) : null}
          <p className="text-sm text-muted-foreground">
            Nechcete hledat?{' '}
            <button
              type="button"
              onClick={() => toManual()}
              className="font-semibold text-action hover:underline"
            >
              Vyplnit ručně
            </button>
          </p>
        </div>
      ) : step === 'found' && found ? (
        <FoundProfile
          found={found}
          status={status}
          onStatus={setStatus}
          onChange={setFound}
          onBack={back}
          onManual={() => toManual(found.name)}
        />
      ) : (
        <ManualForm
          manual={manual}
          categoryError={Boolean(errors.category)}
          status={status}
          onStatus={setStatus}
          onChange={(patch) => {
            setManual((current) => ({ ...current, ...patch }))
            if (patch.category) setErrors((current) => ({ ...current, category: false }))
          }}
          onBack={back}
        />
      )}
    </DetailSheet>
  )
}

function CatalogMatches({
  matches,
  mine,
  onAdd,
  onOpen,
}: {
  matches: CatalogService[]
  mine: (item: CatalogService) => Supplier | undefined
  onAdd: (item: CatalogService) => void
  onOpen: (supplierId: string) => void
}) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        <BookOpen aria-hidden="true" className="size-4 text-action" />
        Je to někdo z katalogu?
      </h3>
      <ul className="space-y-2">
        {matches.map((item) => {
          const existing = mine(item)
          return (
            <li
              key={item.id}
              className="flex items-center gap-4 rounded-2xl bg-panel-accent px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{item.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {categoryLabel(item.category)} · {item.region}
                </p>
              </div>
              {existing ? (
                <Button
                  appearance="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => onOpen(existing.id)}
                >
                  Už ho máte · Otevřít
                </Button>
              ) : (
                <Button size="sm" onClick={() => onAdd(item)}>
                  Přidat z katalogu
                </Button>
              )}
            </li>
          )
        })}
      </ul>
      <p className="text-xs text-muted-foreground">
        Není to on? Klikněte na Najít a podíváme se na internet.
      </p>
    </section>
  )
}

/** The AI's result as a prefilled form: the couple checks it, fixes what's off and adds it. */
function FoundProfile({
  found,
  status,
  onStatus,
  onChange,
  onBack,
  onManual,
}: {
  found: FoundSupplier
  status: SupplierStatus
  onStatus: (status: SupplierStatus) => void
  onChange: (found: FoundSupplier) => void
  onBack: () => void
  onManual: () => void
}) {
  const setProfile = (patch: SupplierProfile) =>
    onChange({ ...found, profile: { ...found.profile, ...patch } })
  return (
    <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
      <section className="flex gap-3 rounded-2xl bg-panel-accent px-4 py-3.5 text-sm">
        <Sparkles aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-action" />
        <div className="space-y-1">
          <p className="font-semibold">Profil jsme vyplnili z internetu</p>
          <p className="text-muted-foreground">
            Zdroj: {found.sources.join(', ')}. Zkontrolujte údaje a případně je opravte.
          </p>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-x-6 gap-y-7">
        <Tile icon={Shapes} label="Kategorie" wide>
          <Select
            variant="soft"
            className={softControl}
            value={found.category}
            onValueChange={(category) => onChange({ ...found, category })}
          >
            {categoryOrder.map((value) => (
              <SelectItem key={value} value={value}>
                {categoryLabel(value)}
              </SelectItem>
            ))}
          </Select>
        </Tile>
        <Tile icon={Flag} label="Stav" wide>
          <Segmented label="Stav" options={startStatuses} value={status} onChange={onStatus} />
        </Tile>
      </div>

      <fieldset className="grid gap-4 sm:grid-cols-2">
        <legend className="sr-only">Veřejný profil</legend>
        <Field label="Popis" className="sm:col-span-2">
          <Textarea
            value={found.profile.description ?? ''}
            onChange={(event) => setProfile({ description: event.target.value })}
            className="min-h-24"
          />
        </Field>
        {foundFields.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            className={field.key === 'address' ? 'sm:col-span-2' : undefined}
          >
            <Input
              value={found.profile[field.key] ?? ''}
              placeholder="Nenašli jsme"
              onChange={(event) => setProfile({ [field.key]: event.target.value })}
            />
          </Field>
        ))}
      </fieldset>

      <p className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
        <button
          type="button"
          onClick={onBack}
          className="font-semibold text-action hover:underline"
        >
          Není to on? Hledat znovu
        </button>
        <button
          type="button"
          onClick={onManual}
          className="font-semibold text-muted-foreground hover:text-foreground hover:underline"
        >
          Vyplnit ručně
        </button>
      </p>
    </div>
  )
}

function ManualForm({
  manual,
  categoryError,
  status,
  onStatus,
  onChange,
  onBack,
}: {
  manual: Manual
  categoryError: boolean
  status: SupplierStatus
  onStatus: (status: SupplierStatus) => void
  onChange: (patch: Partial<Manual>) => void
  onBack: () => void
}) {
  const { contact } = manual
  const setContact = (patch: Partial<Manual['contact']>) =>
    onChange({ contact: { ...contact, ...patch } })
  return (
    <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
      <div className="grid grid-cols-2 gap-x-6 gap-y-7">
        <Tile icon={Shapes} label="Kategorie" wide>
          <Select
            variant="soft"
            className={cn(softControl, categoryError && 'ring-1 ring-error-foreground')}
            value={manual.category ?? undefined}
            placeholder="Vyberte kategorii"
            onValueChange={(category) => onChange({ category })}
          >
            {categoryOrder.map((value) => (
              <SelectItem key={value} value={value}>
                {categoryLabel(value)}
              </SelectItem>
            ))}
          </Select>
          {categoryError ? (
            <p className="px-1 text-xs text-error-foreground">Vyberte kategorii.</p>
          ) : null}
        </Tile>
        <Tile icon={Flag} label="Stav" wide>
          <Segmented label="Stav" options={startStatuses} value={status} onChange={onStatus} />
        </Tile>
        <Tile icon={UserRound} label="Kontaktní osoba" wide>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              value={contact.name}
              placeholder="Jméno"
              aria-label="Jméno kontaktní osoby"
              className={softControl}
              onChange={(event) => setContact({ name: event.target.value })}
            />
            <Input
              type="tel"
              value={contact.phone}
              placeholder="Telefon"
              aria-label="Telefon"
              className={softControl}
              onChange={(event) => setContact({ phone: event.target.value })}
            />
            <Input
              type="email"
              value={contact.email}
              placeholder="E-mail"
              aria-label="E-mail"
              className={softControl}
              onChange={(event) => setContact({ email: event.target.value })}
            />
          </div>
        </Tile>
      </div>

      <section className="space-y-2.5">
        <h3 className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <NotebookPen aria-hidden="true" className="size-4" /> Poznámky
        </h3>
        <Textarea
          value={manual.notes}
          maxLength={3000}
          placeholder="Odkud ho znáte, co jste si napsali, na co se zeptat…"
          onChange={(event) => onChange({ notes: event.target.value })}
          className="min-h-24 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
        />
      </section>

      <p className="text-sm">
        <button
          type="button"
          onClick={onBack}
          className="font-semibold text-action hover:underline"
        >
          Zpět na hledání
        </button>
      </p>
    </div>
  )
}

function Tile({
  icon: Icon,
  label,
  wide = false,
  children,
}: {
  icon: typeof Shapes
  label: string
  wide?: boolean
  children: ReactNode
}) {
  return (
    <div className={cn('space-y-2.5', wide ? 'col-span-2' : 'col-span-2 sm:col-span-1')}>
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className="size-4" aria-hidden="true" />
        {label}
      </span>
      <div className="space-y-1">{children}</div>
    </div>
  )
}
